import React from "react";
import PropTypes from 'prop-types';
import { useTranslation } from "react-i18next";
import * as echarts from "echarts";
import { useMediaQuery } from "@mui/material";
import _ from 'lodash';


const yValueInitialize = (jsonDataY, halfValue) => {
    const configs = [
        { key: 'fraIns', name: 'fragrance' },
        { key: 'aromaIns', name: 'aroma' },
        { key: 'flaIns', name: 'flavor' },
        { key: 'aftIns', name: 'aftertaste' },
        { key: 'acdIns', name: 'acidity' },
        { key: 'swtIns', name: 'sweetness' },
        { key: 'mfIns', name: 'mouthfeel' },
    ];

    const calcValue = (obj) => {
        return (obj?.low ? (-halfValue * (obj?.low/100)) : 0) + (obj?.medium ? (1 - (obj?.medium/100)) : 0) + (obj?.high ? (halfValue * (obj?.high/100)) : 0);
    }

    try {
        return configs.map(cfg => ({
            name: cfg.name,
            value: calcValue(jsonDataY[cfg.key])
        }));
    }
    catch(err) {
        console.error("Error in valueInitialize:", err);
        return [];
    }
};

export const ChartScatter = (props) => {
    const { jsonDataX, jsonDataY, chartName, chartHeight } = props;
    const { t } = useTranslation();
    const isMobile = useMediaQuery('(max-width:600px)');
    const chartRef = React.useRef(null);
    const myChart = React.useRef(null);
    const halfValue = 4500;


    const descriptiveState = (value) => {
        if(value > 400) return  t('assessment.high');
        else if(value < -400) return  t('assessment.low');
        else return  t('assessment.medium');
    }


    React.useEffect(() => {
        if (!myChart.current && chartRef.current) {
            myChart.current = echarts.init(chartRef.current);
        }

        const descriptiveData = yValueInitialize(jsonDataY, halfValue) || [];
        const values = jsonDataX?.map(item => item.value);
        const maxValues = Math.max(...values);
        const halfXValue = parseInt((maxValues + (maxValues * 0.1)) / 2);
        const affectiveData = jsonDataX?.map((item, index) => ({
            xValue: item.value,
            label: t(`assessment.${item.name.toLowerCase().trim().replace(/\s+/g, '')}`),
            name: item.name.toLowerCase().trim().replace(/\s+/g, ''),
            color: item.itemStyle.color,
            idx: index,
        })) || [];

        const dataSet = descriptiveData && affectiveData ?
        affectiveData.filter(obj1 => descriptiveData.some(obj2 => obj2.name === obj1.name.toLowerCase())).sort(item => item.idx).map((item, index) => ({
            type: 'scatter',
            idx: index,
            value2: index + 1,
            id: item.name,
            name: item.label,
            color: item.color,
            itemStyle: { color: item.color, },
            symbolSize: isMobile ? 25 : 55,
            icon: 'circle',
            data: [[item.xValue - halfXValue, descriptiveData.find(itemD => itemD['name'] === item.name)?.value]],
        })) : [];

        const option = {
            grid: {
                top: 50,
                left: Math.round(myChart.current._dom.offsetWidth) < 400 ? 45 : Math.round(myChart.current._dom.offsetWidth) < 600 ? 58 : 80,
                right: Math.round(myChart.current._dom.offsetWidth) < 400 ? 110 : Math.round(myChart.current._dom.offsetWidth) < 600 ? 150 : 180,
                bottom: 50,
                show: false,
            },
            xAxis: {
                name: t('assessment.affective'),
                nameTextStyle: {
                    fontSize: Math.round(myChart.current._dom.offsetWidth) < 400 ? 8 : Math.round(myChart.current._dom.offsetWidth) < 600 ? 12 : 16,
                    fontWeight: '800'
                },
                type: 'value',
                min: -1 * halfXValue,
                max: halfXValue,
                interval: halfXValue,
                nameRotate : -90,
                axisTick: {show: false},
                minorTick: { show: false, },
                splitLine: { show: false, },
                minorSplitLine: { show: false, },
                axisLabel: {
                    formatter: function (val, index) {
                        return descriptiveState(val);
                    },
                    fontSize: Math.round(myChart.current._dom.offsetWidth) < 400 ? 8 : Math.round(myChart.current._dom.offsetWidth) < 600 ? 12 : 16,
                    fontWeight: '500'
                },
                axisLine: {
                    ineStyle: {
                        type:'solid',
                        width: 2,
                    },
                    symbol: 'arrow',
                    symbolSize: [10, 10],
                },
            },
            yAxis: {
                name: t('assessment.descriptive'),
                nameTextStyle: {
                    fontSize: Math.round(myChart.current._dom.offsetWidth) < 400 ? 8 : Math.round(myChart.current._dom.offsetWidth) < 600 ? 12 : 16,
                    fontWeight: '800'
                },
                type: 'value',
                min: -5000,
                max: 5000,
                interval: 5000,
                axisTick: {show: false},
                minorTick: { show: false, },
                splitLine: { show: false, },
                minorSplitLine: { show: false, },
                axisLabel: {
                    formatter: function (val, index) {
                        return descriptiveState(val);
                    },
                    fontSize: Math.round(myChart.current._dom.offsetWidth) < 400 ? 8 : Math.round(myChart.current._dom.offsetWidth) < 600 ? 12 : 16,
                    fontWeight: '500'
                },
                axisLine: {
                    ineStyle: {
                        type:'solid',
                        width: 2,
                    },
                    symbol: 'arrow',
                    symbolSize: [10, 10],
                },
            },
            legend: {
                show: 'true',
                orient: 'vertical', // horizontal
                right: Math.round(myChart.current._dom.offsetWidth) < 400 ? '2' : Math.round(myChart.current._dom.offsetWidth) < 600 ? '10' :'20',
                top: 'center',
                data: dataSet,
                backgroundColor: '#efefef',
                textStyle: {
                    color: '#131213',
                    fontSize: Math.round(myChart.current._dom.offsetWidth) < 400 ? 8 : Math.round(myChart.current._dom.offsetWidth) < 600 ? 12 : 16,
                    lineHeight: 30,
                }
            },
            series: dataSet,
            tooltip: {
                position: 'bottom',
                formatter: function (params) {
                    return '<span style="font-weight: 800; color:"' + params.color + '">' + params.seriesName + '</span><br />' + t('assessment.affective') + ': ' + (params.data[0] + halfXValue) + '<br />' + t('assessment.descriptive') + ': ' + descriptiveState(params.data[1]);
                },
            },
            backgroundColor: {
                type: 'radial',
                x: 0.3,
                y: 0.3,
                r: 0.8,
                colorStops: [{
                    offset: 0,
                    color: '#F7F7F7'
                },{
                    offset: 1,
                    color: '#FFFFFF'
                }]
            },
        };

        if (myChart.current) {
            myChart.current.showLoading();
            myChart.current.setOption(option);
            myChart.current.hideLoading();
        }
        
        
        // 창크기 변경할때 카트를 갱신한다.
        const handleResize = _.debounce(() => {
            if (myChart.current) {
                myChart.current.showLoading();
                myChart.current.setOption(option);
                myChart.current?.resize();
                myChart.current.hideLoading();
            }
        }, 50); // Debounce for 50ms

        window.addEventListener('resize', handleResize);

        return () => {
            try {
                window.removeEventListener('resize', handleResize);
            
                myChart.current && myChart.current.dispose();
                myChart.current && myChart.current.dispose();
                myChart.current = null;
            }
            catch (err) {
                console.log("Unload", new Date().toISOString(), JSON.stringify(err), err);
            }
        };
    }, [jsonDataX, jsonDataY, chartName, chartHeight]);


    return <div ref={chartRef} style={{ width: "100%", height: chartHeight || "600px" }} />;
};

ChartScatter.propTypes = {
    jsonDataX: PropTypes.oneOfType([
        PropTypes.array,
        PropTypes.func,
        PropTypes.object,
    ]).isRequired,
    jsonDataY: PropTypes.oneOfType([
        PropTypes.array,
        PropTypes.func,
        PropTypes.object,
    ]).isRequired,
    chartName: PropTypes.string,
    chartHeight: PropTypes.string,
};
